/* eslint-disable jsdoc/require-jsdoc */
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import displayedPrice, {
  hasDisplayTotal,
  netFeePerPassenger,
  netPricingView,
} from 'utils/trips/displayedPrice';
import { usePriceWithoutFeesVariant } from 'utils/trips/priceWithoutFeesExperiment';
import {
  finalPrice,
  finalTotalBeforeDiscount,
  isPrice,
  markupDeltaBetween,
} from 'utils/trips/finalPricing';
import { PRE_CHECKOUT_STEPS } from 'constants/purchaseSteps';
import getPurchaseStepName from 'utils/purchase/getPurchaseStepName';
import { parseFixedFee } from 'utils/purchase/parseFixedFee';
import removeAdjacentSeats from 'utils/purchase/removeAdjacentSeats';
import { purchaseLegPricingKey, returnLegEarnsRoundTripFare } from 'utils/roundTripFare';
import { formatPercent } from 'utils/Helpers';
import { digitalDiscountsTypes } from 'models/parseTrip';
import useMixDiscountFaresByAvailability from 'hooks/useMixDiscountFaresByAvailability';
import usePreCheckoutFinalPrice from 'hooks/usePreCheckoutFinalPrice';
import {
  isBfoPricing,
  isBfoSupportedJourney,
  resolveBfoEffectiveSeatPrice,
} from 'utils/purchase/baseFareOverride';

/**
 * Converts passenger selection to a simplified format based on GrowthBook feature.
 * @param {Object} passengerSelection - The original passenger selection object.
 * @param {string} typeCategory - The category type ('simple' or 'categories'). When 'simple', collapses to { general: total }.
 * @returns {Object} The simplified passenger selection object.
 */
const convertToSimple = (passengerSelection, typeCategory) => {
  if (typeCategory === 'simple') {
    const total = Object.values(passengerSelection).reduce((acc, value) => acc + value, 0);
    return { general: total };
  }
  return passengerSelection;
};

/**
 * Optimized pricing calculation for overbooking scenarios
 * @param {object} options - The options for calculating overbooking pricing
 * @param {number} options.passengersCount - Total number of passengers
 * @param {Array} options.passengerTypes - Array of passenger types with pricing
 * @param {object} options.pricing - Original pricing object for base structure
 * @param {boolean} [options.netFeesForDisplay] - Whether this user is in the INS-1288 variant arm.
 * @returns {object} Complete pricing breakdown for overbooking
 */
const calculateOverbookingPricing = ({
  passengersCount,
  passengerTypes,
  pricing,
  netFeesForDisplay = false,
}) => {
  const oversoldType = passengerTypes.find((type) => type.type === 'oversold');

  if (!oversoldType) {
    // Fallback structure if no oversold type found
    return {
      amount: 0,
      total: 0,
      totalPercentDiscount: '0%',
      totalBeforeDiscount: 0,
      discount: 0,
      discountType: null,
      passengersCount: 0,
      luggage: 0,
      taxes: 0,
      networks: 0,
      insurance: 0,
      passengerBreakdown: { general: { count: 0, total: 0 } },
      hasSingleTripDiscount: false,
      singleTripDiscount: null,
      // Overbooking prices come from the oversold passenger type, which the API
      // sends without a platform-usage fee (MID-747).
      fixedFee: null,
    };
  }

  // INS-1288: this branch returns before the netting above it ever reaches the figures, because
  // it rebuilds them from the raw `passengerTypes` entry rather than from `pricing`. The entry's
  // OWN `displayTotal` is what to read, never the shared per-passenger delta: the oversold fare
  // arrives without the platform-usage fee (MID-747, see the fallback's `fixedFee: null` above),
  // so subtracting a delta derived from the headline could take the same charge out twice.
  // Byte-identical in the control arm, where `displayedPrice` returns `total`. (@luisAzcuaga)
  const oversoldPrice = Number(displayedPrice(oversoldType, netFeesForDisplay)) || 0;
  const total = oversoldPrice * passengersCount;

  // Create breakdown for all passenger types (set to 0 except oversold)
  const passengerBreakdown = {};

  // Initialize all passenger types to 0
  passengerTypes.forEach((type) => {
    passengerBreakdown[type.type] = {
      count: 0,
      total: 0,
      discount: 0,
      discountPercent:
        type.type !== 'general'
          ? formatPercent(pricing.totalBeforeDiscount || 0, Number(type.total) || 0)
          : null,
    };
  });

  // Set general breakdown
  passengerBreakdown.general = {
    count: 0,
    total: 0,
    totalSpecialDiscount: 0,
    countWithSpecialDiscount: 0,
    countWithoutSpecialDiscount: 0,
    normalTotal: 0,
    discount: 0,
    discountPercent: null,
    normalDiscountPercent: null,
  };

  // Set oversold breakdown
  passengerBreakdown.oversold = {
    count: passengersCount,
    total,
    totalSpecialDiscount: total,
    countWithSpecialDiscount: passengersCount,
    countWithoutSpecialDiscount: 0,
    normalTotal: 0,
    discount: 0,
    discountPercent: null,
    normalDiscountPercent: null,
  };

  return {
    amount: total,
    total,
    totalPercentDiscount: '0%',
    totalBeforeDiscount: total,
    discount: 0,
    discountType: pricing.discountType || null,
    passengersCount,
    luggage: 0,
    taxes: 0,
    networks: 0,
    insurance: 0,
    passengerBreakdown,
    hasSingleTripDiscount: false,
    singleTripDiscount: null,
    // Same as the fallback above: the oversold type carries no platform-usage fee.
    fixedFee: null,
  };
};

/**
 * One seat's fare as it should be displayed.
 *
 * `seat.price` is a gross per-seat fare -- search ships `displayTotal` on the pricing object and
 * never on the seat map -- so both places that add seats up have to take the same per-passenger delta
 * out of it, or the seats screen shows two different totals for one selection (INS-1288).
 * Floored at 0 so a seat cheaper than the delta never turns negative. A seat with no fare of its
 * own falls back to the trip price, which the caller has already netted.
 *
 * @param {object} seat - A selected seat.
 * @param {number} feePerPassenger - The shared delta, from `netFeePerPassenger`.
 * @param {number} defaultFare - Already-netted fare for a seat that carries none.
 * @returns {number} The fare to add up.
 */
const netSeatFare = (seat, feePerPassenger, defaultFare) =>
  seat?.price == null ? defaultFare : Math.max(Number(seat.price) - feePerPassenger, 0);

/**
 * BOOK-168: the same pricing object with every price this module reads replaced by its
 * markup-inclusive companion, so the whole pre-checkout derivation runs on the final arm.
 *
 * The same approach as INS-1288's `netPricingView`, for the same reason: switching only the
 * headline leaves the crossed-out price, the discount, the fallback discounts and the fare
 * allocation on base, and a panel whose rows come from one arm and whose total from the other
 * does not add up. Working off one view at the source keeps every sum -- across seats, fare
 * segments, passenger categories -- inside one arm by construction (design D5).
 *
 * - `total`, `totalBeforeDiscount` and each provider discount's `total` read their companion
 *   (`companion ?? base`), so a fare without one keeps control's figure, never `NaN` (D8).
 * - A discount amount is derived from companions only: `totalBeforeDiscountWithMarkup -
 *   totalWithMarkup`. Without both there is nothing final to derive it from, so the amount sent
 *   stays, as it does in control.
 * - A provider discount's `amount` carries that entry's markup, because `amount + taxes` is how the
 *   category rows compare a digital discount against the general fare.
 * - Taxes, the breakdown and the fixed fee are left alone: the markup is none of them.
 *
 * Idempotent, and returns the object untouched for control, so control figures are unchanged.
 *
 * @param {object} [pricing] - A search pricing object, as the purchase fragment carries it.
 * @param {boolean} [isFinal] - Whether this purchase is in the final arm.
 * @returns {object} The final view, or the original object in control.
 */
export const finalPricingView = (pricing, isFinal = false) => {
  if (!isFinal || !pricing) return pricing;

  const derivedDiscount =
    isPrice(pricing.totalWithMarkup) && isPrice(pricing.totalBeforeDiscountWithMarkup)
      ? Math.max(Number(pricing.totalBeforeDiscountWithMarkup) - Number(pricing.totalWithMarkup), 0)
      : null;
  const hasDerivedDiscount = derivedDiscount !== null;

  /**
   * One provider discount on the final arm. Only records that carry a `total` are rewritten, for
   * the reason `netPricingView` gives: the `single_trip_discount` validation tests `'total' in`.
   *
   * @param {object} discount - A provider discount record.
   * @returns {object} The record with its companion as its total.
   */
  const finalDiscount = (discount) => {
    if (!discount || !Object.prototype.hasOwnProperty.call(discount, 'total')) return discount;

    const markup = markupDeltaBetween(discount.total, discount.totalWithMarkup);

    return {
      ...discount,
      total: finalPrice(discount, true),
      ...(markup && isPrice(discount.amount) ? { amount: Number(discount.amount) + markup } : {}),
    };
  };

  return {
    ...pricing,
    total: finalPrice(pricing, true),
    totalBeforeDiscount: finalTotalBeforeDiscount(pricing),
    discountBase:
      hasDerivedDiscount && pricing.discountBase != null ? derivedDiscount : pricing.discountBase,
    providerDiscount:
      hasDerivedDiscount && pricing.providerDiscount && typeof pricing.providerDiscount === 'object'
        ? { ...pricing.providerDiscount, amount: derivedDiscount }
        : pricing.providerDiscount,
    providerDiscounts: Array.isArray(pricing.providerDiscounts)
      ? pricing.providerDiscounts.map(finalDiscount)
      : pricing.providerDiscounts,
  };
};

/**
 * BOOK-168: passenger types with each category priced at its own companion. The backend
 * annotates every `passenger_types[]` entry separately because a percent rule on a discounted
 * fare yields a smaller fee than on the general one, so each category keeps its own.
 *
 * @param {Array<object>} [passengerTypes] - Passenger types with their per-category pricing.
 * @returns {Array<object>|undefined} The same entries on the final arm.
 */
const finalPassengerTypes = (passengerTypes) =>
  Array.isArray(passengerTypes)
    ? passengerTypes.map((passengerType) => ({
        ...passengerType,
        total: finalPrice(passengerType, true),
      }))
    : passengerTypes;

/**
 * Gets the remaining availability for the active discount in a pricing object.
 * @param {object} pricing - Trip pricing object.
 * @returns {number|null} Active discount availability, or null when uncapped.
 */
export const getDiscountAvailability = (pricing = {}) => {
  if (pricing?.discountAvailability != null) {
    const topLevelAvailability = Number(pricing.discountAvailability);
    if (Number.isFinite(topLevelAvailability)) return Math.max(topLevelAvailability, 0);
  }

  const currentDiscountName = pricing?.discountType || pricing?.name;
  const currentDiscount = (pricing?.providerDiscounts || []).find(
    (discount) => discount.name === currentDiscountName,
  );
  const providerAvailability = Number(currentDiscount?.availability);

  return Number.isFinite(providerAvailability) ? Math.max(providerAvailability, 0) : null;
};

/**
 * Gets a discount availability from a provider discount object.
 * @param {object} discount - Provider discount object.
 * @returns {number|null} Discount availability, or null when uncapped.
 */
const getProviderDiscountAvailability = (discount = {}) => {
  if (discount?.availability == null) return null;

  const availability = Number(discount.availability);
  return Number.isFinite(availability) ? Math.max(availability, 0) : null;
};

/**
 * Gets digital provider discounts that can be used after the active discount.
 * @param {object} pricing - Trip pricing object.
 * @returns {Array} Provider discounts available as fallback tiers.
 */
const getFallbackDiscountCandidates = (pricing = {}) => {
  const currentDiscountName = pricing?.discountType || pricing?.name;

  return (pricing?.providerDiscounts || []).filter((discount) => {
    if (discount.name === currentDiscountName) return false;
    if (!digitalDiscountsTypes.includes(discount.name)) return false;
    if (discount.total == null) return false;

    return true;
  });
};

/**
 * Gets the next digital discount that can cover the whole selected quantity.
 * @param {object} options - Helper options.
 * @param {object} options.pricing - Trip pricing object.
 * @param {number} options.selectedCount - Total selected seats for the trip.
 * @returns {object|undefined} Fallback provider discount.
 */
export const getFallbackDiscountForSelection = ({ pricing = {}, selectedCount = 0 }) => {
  const normalizedSelectedCount = Number(selectedCount);

  return getFallbackDiscountCandidates(pricing).find((discount) => {
    const availability = getProviderDiscountAvailability(discount);
    return availability === null || availability >= normalizedSelectedCount;
  });
};

/**
 * Resolves how general passengers should be assigned across discounted, fallback, and regular fares.
 * @param {object} options - Helper options.
 * @param {object} options.pricing - Trip pricing object.
 * @param {number} options.selectedCount - Total general passengers selected.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether mixed fares are enabled.
 * @param {number} options.activeUnitAmount - Active discount amount without taxes.
 * @param {number} options.regularUnitAmount - Regular amount without taxes.
 * @returns {object} Fare assignment details.
 */
export const resolveFareAllocation = ({
  pricing = {},
  selectedCount = 0,
  mixDiscountFaresByAvailability = false,
  activeUnitAmount,
  regularUnitAmount,
}) => {
  const normalizedSelectedCount = Math.max(Number(selectedCount) || 0, 0);
  const discountedUnitTotal = Number(pricing.total || 0);
  const regularUnitTotal = Number(pricing.totalBeforeDiscount ?? pricing.total ?? 0);
  const discountedUnitAmount = Number(activeUnitAmount ?? pricing.amount ?? discountedUnitTotal);
  const resolvedRegularUnitAmount = Number(regularUnitAmount ?? regularUnitTotal);
  const discountAvailability = getDiscountAvailability(pricing);
  const hasValidDiscount = Boolean(pricing.providerDiscount && discountAvailability !== null);
  const discountAvailabilityExceeded =
    hasValidDiscount && normalizedSelectedCount > discountAvailability;
  const shouldMixDiscountFares =
    mixDiscountFaresByAvailability && discountAvailabilityExceeded && discountAvailability > 0;
  const fallbackDiscount =
    discountAvailabilityExceeded && !shouldMixDiscountFares
      ? getFallbackDiscountForSelection({ pricing, selectedCount: normalizedSelectedCount })
      : null;
  const resolvedDiscountUnitTotal = Number(fallbackDiscount?.total ?? discountedUnitTotal);
  const resolvedDiscountUnitAmount = Number(
    fallbackDiscount?.amount ?? activeUnitAmount ?? pricing.amount ?? resolvedDiscountUnitTotal,
  );

  const fareSegments = [];

  const addFareSegment = ({ type, name, count, unitTotal, unitAmount }) => {
    const normalizedCount = Math.max(Number(count) || 0, 0);
    if (!normalizedCount) return;

    fareSegments.push({
      type,
      name,
      count: normalizedCount,
      unitTotal,
      unitAmount,
      total: unitTotal * normalizedCount,
      amount: unitAmount * normalizedCount,
      discount: Math.max(regularUnitTotal - unitTotal, 0) * normalizedCount,
    });
  };

  if (shouldMixDiscountFares) {
    let remainingCount = normalizedSelectedCount;
    const activeDiscountedCount = Math.min(remainingCount, discountAvailability);

    addFareSegment({
      type: 'active_discount',
      name: pricing.discountType || pricing.name,
      count: activeDiscountedCount,
      unitTotal: discountedUnitTotal,
      unitAmount: discountedUnitAmount,
    });
    remainingCount -= activeDiscountedCount;

    getFallbackDiscountCandidates(pricing).some((discount) => {
      if (!remainingCount) return true;

      const availability = getProviderDiscountAvailability(discount);
      const fallbackCount =
        availability === null ? remainingCount : Math.min(remainingCount, availability);

      addFareSegment({
        type: 'fallback_discount',
        name: discount.name,
        count: fallbackCount,
        unitTotal: Number(discount.total),
        unitAmount: Number(discount.amount ?? discount.total),
      });
      remainingCount -= fallbackCount;

      return remainingCount === 0;
    });

    addFareSegment({
      type: 'regular',
      count: remainingCount,
      unitTotal: regularUnitTotal,
      unitAmount: resolvedRegularUnitAmount,
    });
  } else if (hasValidDiscount && (!discountAvailabilityExceeded || fallbackDiscount)) {
    addFareSegment({
      type: fallbackDiscount ? 'fallback_discount' : 'active_discount',
      name: fallbackDiscount?.name ?? pricing.discountType ?? pricing.name,
      count: normalizedSelectedCount,
      unitTotal: resolvedDiscountUnitTotal,
      unitAmount: resolvedDiscountUnitAmount,
    });
  } else {
    addFareSegment({
      type: 'regular',
      count: normalizedSelectedCount,
      unitTotal: regularUnitTotal,
      unitAmount: resolvedRegularUnitAmount,
    });
  }

  const discountedSegments = fareSegments.filter((segment) => segment.type !== 'regular');
  const regularSegments = fareSegments.filter((segment) => segment.type === 'regular');
  const discountedCount = discountedSegments.reduce((sum, segment) => sum + segment.count, 0);
  const activeDiscountedCount = fareSegments
    .filter((segment) => segment.type === 'active_discount')
    .reduce((sum, segment) => sum + segment.count, 0);
  const activeDiscountedTotal = fareSegments
    .filter((segment) => segment.type === 'active_discount')
    .reduce((sum, segment) => sum + segment.total, 0);
  const fallbackDiscountedCount = fareSegments
    .filter((segment) => segment.type === 'fallback_discount')
    .reduce((sum, segment) => sum + segment.count, 0);
  const regularCount = regularSegments.reduce((sum, segment) => sum + segment.count, 0);
  const totalWithDiscount = discountedSegments.reduce((sum, segment) => sum + segment.total, 0);
  const totalWithoutDiscount = regularSegments.reduce((sum, segment) => sum + segment.total, 0);
  const total = totalWithDiscount + totalWithoutDiscount;
  const amount = fareSegments.reduce((sum, segment) => sum + segment.amount, 0);
  const totalBeforeDiscount = regularUnitTotal * normalizedSelectedCount;
  const discount = fareSegments.reduce((sum, segment) => sum + segment.discount, 0);
  const unitDiscountAmount = Math.max(regularUnitTotal - resolvedDiscountUnitTotal, 0);
  const blendedDiscountPercent =
    totalBeforeDiscount && total ? formatPercent(totalBeforeDiscount, total) : 0;
  const perSeatTotals = fareSegments.flatMap((segment) => {
    return Array.from({ length: segment.count }, () => segment.unitTotal);
  });

  return {
    discountAvailability,
    discountAvailabilityExceeded,
    shouldMixDiscountFares,
    fallbackDiscount,
    fareSegments,
    discountedCount,
    activeDiscountedCount,
    activeDiscountedTotal,
    fallbackDiscountedCount,
    regularCount,
    discountedUnitTotal: resolvedDiscountUnitTotal,
    regularUnitTotal,
    totalWithDiscount,
    totalWithoutDiscount,
    total,
    amount,
    discount,
    unitDiscountAmount,
    blendedDiscountPercent,
    perSeatTotals,
    hasDiscountedFare: discount > 0,
  };
};

/**
 * Calculate the pricing for a trip.
 * @param {object} options - The options for calculating the pricing.
 * @param {object} options.pricing - The pricing information for the trip.
 * @param {number} options.passengersCount - The number of passengers.
 * @param {string} options.typeCategory - The category type for pricing calculation.
 * @param {object} options.passengerSelection - Contains the selected passengers in search.
 * @param {Array} options.passengerTypes - Contains the pricing for each passenger type.
 * @param {boolean} options.overbooking - If true, additional passengers will be categorized as 'oversold'.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @returns {object} The calculated trip pricing.
 */

const HALF_PRICE_FALLBACK_PAIRS = {
  older: 'older_half',
  disadvantaged_youth: 'disadvantaged_youth_half',
};

/**
 * Moves overflowed passengers of a gratuity type into its half-price variant
 * when slots remain. Mutates the accumulator counts in place.
 *
 * @param {object} acc - Accumulator with passenger counts per type.
 * @param {string} key - Passenger type whose selection exceeded availability.
 * @param {object} passengerTypesMap - Passenger types indexed by type key.
 * @param {number} overflow - Passengers exceeding the original type availability.
 * @returns {void}
 */
const distributeHalfPriceOverflow = (acc, key, passengerTypesMap, overflow) => {
  const halfKey = HALF_PRICE_FALLBACK_PAIRS[key];
  if (!halfKey) return;

  const halfAvailability = passengerTypesMap[halfKey]?.availability ?? 0;
  const currentHalfCount = acc[halfKey] || 0;
  const remainingHalfAvailability = Math.max(halfAvailability - currentHalfCount, 0);
  if (remainingHalfAvailability <= 0) return;

  // Move as many overflowed passengers as possible into the half-price variant
  const halfSlots = Math.min(overflow, remainingHalfAvailability);

  acc[halfKey] = currentHalfCount + halfSlots;
  acc.general -= halfSlots;
};

const computeTripPricing = ({
  pricing: rawPricing = {},
  passengersCount,
  passengerSelection = {},
  passengerTypes = [],
  typeCategory = 'categories',
  overbooking = false,
  halfPriceFallbackEnabled = false,
  mixDiscountFaresByAvailability = false,
  netFeesForDisplay = false,
}) => {
  // INS-1288: every figure this hook derives for display -- the headline, the crossed-out
  // "before" price, the discount percentage and the fare allocation -- must come from the
  // netted unit price, not just the headline. Netting only `total` made the seat panel
  // believe there was a discount (it rendered "1 Passageiro R$ 172" above a netted R$ 151)
  // and, on trips with two or more passenger types, the allocation overwrote the netted
  // total with a gross one. Working off a netted view of the pricing object fixes both at
  // the source. Returns the object untouched for the control arm of the A/B test, and when the
  // backend shipped no `displayTotal`.
  const pricing = netPricingView(rawPricing, netFeesForDisplay);

  // Handle overbooking scenario first - early return for efficiency
  if (overbooking && passengersCount > 0) {
    return calculateOverbookingPricing({
      passengersCount,
      passengerTypes,
      pricing,
      netFeesForDisplay,
    });
  }

  if (passengerSelection.adult) {
    passengerSelection.general = passengerSelection.adult;
    delete passengerSelection.adult;
  }
  passengerSelection = convertToSimple(passengerSelection, typeCategory);

  // Index passenger types by type key for O(1) lookup during half-price overflow distribution
  const passengerTypesMap = halfPriceFallbackEnabled
    ? passengerTypes.reduce((acc, pt) => ({ ...acc, [pt.type]: pt }), {})
    : {};

  const passengerAvailabilityOrder = passengerTypes.reduce(
    (acc, item) => {
      const key = item.type;

      // Check if the key exists in the acc object (accumulator) and the availability is lower
      if (acc[key] !== undefined && item.availability < acc[key]) {
        const overflow = acc[key] - item.availability;
        acc.general += overflow;
        acc[key] = item.availability;

        // Redirect overflow to half-price variant (e.g. older → older_half) before falling back to general
        if (halfPriceFallbackEnabled)
          distributeHalfPriceOverflow(acc, key, passengerTypesMap, overflow);
      }

      return acc;
    },
    { ...passengerSelection },
  );

  const generalCount = passengerAvailabilityOrder?.general || 0;
  const totalSelectionCount = Object.values(passengerAvailabilityOrder).reduce(
    (acc, val) => acc + val,
    0,
  );
  // Validate if the pricing has the single_trip_discount structure
  const hasSingleTripDiscount =
    pricing.providerDiscounts &&
    pricing.providerDiscounts.some(
      (discount) =>
        discount.name === 'single_trip_discount' &&
        'amount' in discount &&
        'taxes' in discount &&
        'total' in discount,
    );

  // Gets the normal price with digital discount or use the normal price
  const digitalDiscount =
    pricing.providerDiscounts &&
    pricing.providerDiscounts.find(
      (discount) =>
        digitalDiscountsTypes.includes(discount.name) || discount.name === 'single_trip_discount',
    );

  // General fare used when no discount can cover the full selection.
  const generalFareTotal = Number(pricing.totalBeforeDiscount ?? pricing.total ?? 0);

  const totalPerGeneral = pricing.total || 0;
  const amountPerPassenger = pricing.amount || 0;
  const discountPerPassenger = pricing.providerDiscount?.amount || 0;
  const breakdown = pricing.breakdown || {};
  const luggagePerPassenger = breakdown.equipaje || 0;
  const networksPerPassenger = breakdown.redes || 0;
  const insurancePerPassenger = breakdown.seguros || 0;
  const taxesFromBreakdown = breakdown.impuestos || 0;
  const taxesFromPricing = pricing.taxes || 0;
  const taxesPerPassenger = taxesFromPricing > 0 ? taxesFromPricing : taxesFromBreakdown;
  const normalAmountPerPassenger = Math.max(generalFareTotal - taxesPerPassenger, 0);

  // Calculate costs for general passengers
  const generalPassengerCount = Math.min(passengersCount, generalCount);
  const additionalPassengerCount = Math.max(passengersCount - totalSelectionCount, 0);
  const totalGeneralCount =
    passengerTypes.length <= 1 ? passengersCount : generalPassengerCount + additionalPassengerCount;

  let total =
    passengerTypes.length <= 1
      ? passengersCount * totalPerGeneral
      : totalPerGeneral * totalGeneralCount;
  let amount = amountPerPassenger * totalGeneralCount;
  let discount = discountPerPassenger * totalGeneralCount;

  // Calculate costs for other passenger types

  const fareAllocation = resolveFareAllocation({
    pricing,
    selectedCount: totalGeneralCount,
    mixDiscountFaresByAvailability,
    activeUnitAmount: amountPerPassenger,
    regularUnitAmount: normalAmountPerPassenger,
  });
  const {
    discountAvailabilityExceeded,
    shouldMixDiscountFares,
    fallbackDiscount,
    fareSegments,
    activeDiscountedCount,
    activeDiscountedTotal,
    discountedUnitTotal,
    discountedCount: generalWitSpecialDiscountCount,
    totalWithDiscount: generalTotalWithDiscount,
    totalWithoutDiscount: generalTotalWithoutDiscount,
    blendedDiscountPercent,
  } = fareAllocation;
  const selectedDiscountTotal = discountedUnitTotal;

  if (shouldMixDiscountFares) {
    total = fareAllocation.total;
    amount = fareAllocation.amount;
    discount = fareAllocation.discount;
  } else if (discountAvailabilityExceeded) {
    total = generalTotalWithDiscount + generalTotalWithoutDiscount;
    amount = fareAllocation.amount;
    discount = fallbackDiscount ? fareAllocation.discount : 0;
  } else if (passengerTypes.length >= 2) {
    total = generalTotalWithDiscount + generalTotalWithoutDiscount;
  }

  const generalSpecialDiscountCount = shouldMixDiscountFares
    ? activeDiscountedCount
    : generalWitSpecialDiscountCount;
  const generalSpecialDiscountTotal = shouldMixDiscountFares
    ? activeDiscountedTotal
    : generalTotalWithDiscount;
  const generalRegularCount = totalGeneralCount - generalSpecialDiscountCount;
  const generalRegularTotal = total - generalSpecialDiscountTotal;
  const pricingSegments =
    shouldMixDiscountFares && fareSegments.length > 1
      ? fareSegments.map((segment) => ({
          id: `${segment.type}-${segment.name || 'regular'}-${segment.unitTotal}`,
          count: segment.count,
          total: segment.total,
          discountPercent: segment.discount
            ? formatPercent(generalFareTotal, segment.unitTotal)
            : null,
          hasDiscountIcon: segment.type === 'active_discount',
        }))
      : null;

  let generalDiscountPercent = formatPercent(pricing.totalBeforeDiscount, selectedDiscountTotal);
  if (
    !pricing.providerDiscount ||
    (discountAvailabilityExceeded && !fallbackDiscount && !shouldMixDiscountFares)
  ) {
    generalDiscountPercent = null;
  } else if (shouldMixDiscountFares) {
    generalDiscountPercent = blendedDiscountPercent;
  }

  const passengerBreakdown = {
    general: {
      count: totalGeneralCount,
      total,
      totalSpecialDiscount: generalSpecialDiscountTotal,
      countWithSpecialDiscount: generalSpecialDiscountCount,
      countWithoutSpecialDiscount: generalRegularCount,
      normalTotal: generalRegularTotal,
      discount,
      discountPercent: generalDiscountPercent,
      pricingSegments,
      normalDiscountPercent:
        !discountAvailabilityExceeded &&
        passengerTypes.length >= 2 &&
        generalTotalWithoutDiscount &&
        digitalDiscount
          ? formatPercent(digitalDiscount.amount + digitalDiscount.taxes, generalFareTotal)
          : null,
    },
  };

  let remainingPassengersCount = passengersCount - totalGeneralCount;

  passengerTypes.forEach((passengerType) => {
    // Same netting as the general fare above, so the category rows keep summing to the
    // headline total (INS-1288).
    const { type } = passengerType;
    const categoryPrice = displayedPrice(passengerType, netFeesForDisplay);
    if (type === 'general') {
      return;
    }
    const totalPerType = parseFloat(categoryPrice) || 0;
    const typeCount = Math.min(remainingPassengersCount, passengerAvailabilityOrder[type] || 0);
    total += totalPerType * typeCount;
    amount += totalPerType * typeCount;

    passengerBreakdown[type] = {
      count: typeCount,
      total: totalPerType * typeCount,
      discount: 0,
      discountPercent: formatPercent(pricing.totalBeforeDiscount, totalPerType),
    };
    remainingPassengersCount -= typeCount;
  });

  let totalPercentDiscount = 0;
  if (shouldMixDiscountFares && pricing.totalBeforeDiscount) {
    totalPercentDiscount = blendedDiscountPercent;
  } else if ((!discountAvailabilityExceeded || fallbackDiscount) && pricing.totalBeforeDiscount) {
    totalPercentDiscount = formatPercent(pricing.totalBeforeDiscount, selectedDiscountTotal);
  }

  return {
    amount,
    total,
    totalPercentDiscount,
    totalBeforeDiscount: (pricing.totalBeforeDiscount || 0) * passengersCount,
    discount,
    discountType: pricing.discountType,
    passengersCount,
    luggage: luggagePerPassenger * passengersCount,
    taxes: taxesPerPassenger * passengersCount,
    networks: networksPerPassenger * passengersCount,
    insurance: insurancePerPassenger * passengersCount,
    passengerBreakdown,
    fareAllocation,
    hasSingleTripDiscount,
    singleTripDiscount: hasSingleTripDiscount ? digitalDiscount : null,
    // Per-seat platform-usage fee breakdown from the API ({ amount, tax }, already
    // camelized from `fixed_fee`). Only normalized, never recomputed — the fee is
    // already included in `pricing.total`.
    fixedFee: parseFixedFee(pricing.fixedFee),
  };
};

/**
 * Calculate the pricing for a trip, in the arm the purchase resolved.
 *
 * Control (and INS-1288's netted arm) runs the derivation once, exactly as before. In the final
 * arm (BOOK-168) it runs on the final view of the same pricing and passenger types, so each fare
 * -- general, fallback discount, pre-discount, every category, oversold -- carries its own
 * markup, and the caller picks how to present it:
 * - by default, every row stays on base and the markup is exposed once as `serviceFees`, with a
 *   `total` that includes it: the sidebar's non-per-passenger layout renders it as its "Cargo por
 *   servicio" row;
 * - with `inlineMarkup`, every figure is final and there is no service-charge amount: the seats
 *   panel paints the markup inside each row, so its rows add up to its total.
 *
 * Either way the markup is counted once, and a payload without companions yields control's figures.
 *
 * @param {object} options - The derivation's options (`pricing`, `passengersCount`,
 *   `passengerSelection`, `passengerTypes`, `typeCategory`, `overbooking`,
 *   `halfPriceFallbackEnabled`, `mixDiscountFaresByAvailability`, `netFeesForDisplay`), plus:
 * @param {boolean} [options.showFinalPrice] - Whether this purchase is in the final arm.
 * @param {boolean} [options.inlineMarkup] - Paint the markup inside every row instead of as a
 *   separate service-charge amount.
 * @returns {object} The calculated trip pricing.
 */
export const getTripPricing = ({ showFinalPrice = false, inlineMarkup = false, ...options }) => {
  if (!showFinalPrice) return computeTripPricing(options);

  const finalTripPricing = computeTripPricing({
    ...options,
    pricing: finalPricingView(options.pricing, true),
    passengerTypes: finalPassengerTypes(options.passengerTypes),
  });
  if (inlineMarkup) return finalTripPricing;

  const basePricing = computeTripPricing(options);
  const serviceFees = markupDeltaBetween(basePricing.total, finalTripPricing.total);

  return {
    ...basePricing,
    total: serviceFees ? finalTripPricing.total : basePricing.total,
    serviceFees,
  };
};

/**
 * Calculate trip pricing based on individual seat prices
 * @param {object} options - The options for calculating the pricing
 * @param {Array} options.selectedSeats - Array of selected seats with their prices
 * @param {object} options.pricing - Original pricing object for tax/discount info
 * @param {boolean} [options.netFeesForDisplay] - INS-1288 arm, as resolved for this trip.
 * @param {boolean} [options.useBaseFareOverride] - Whether supported canonical BFO may apply.
 * @returns {object} The calculated trip pricing
 */
export const getTripPricingBySeatPrice = ({
  selectedSeats = [],
  pricing = {},
  netFeesForDisplay = false,
  useBaseFareOverride = false,
}) => {
  const regularSeats = removeAdjacentSeats(selectedSeats);
  const adjacentSeats = selectedSeats.filter((seat) => seat.isPickedAsAdjacent);

  // INS-1288: `seat.price` is a gross per-seat fare — search ships `displayTotal` on the pricing
  // object, never on the seat map — so summing it as-is would put a gross panel next to the
  // netted card it expands from, the one disagreement this capability forbids. Take out the
  // same shared per-passenger delta the category path uses (`total - displayTotal`, from the
  // backend's own figures, floored at 0) rather than re-deriving anything per zone: the
  // charges are per passenger, not per seat. Control, or a payload with no `displayTotal`, leaves
  // `netPricingView` returning `pricing` untouched and the delta at 0, so every tenant on
  // seat-based pricing today is byte-identical.
  const displayPricing = netPricingView(pricing, netFeesForDisplay);
  const feePerPassenger = netFeePerPassenger(pricing, netFeesForDisplay);

  const defaultSeatPrice = Number(displayPricing.total ?? displayPricing.amount ?? 0);
  const seatPrice = (seat) => {
    const price = useBaseFareOverride
      ? resolveBfoEffectiveSeatPrice({ pricing, seat })
      : seat.price;
    return netSeatFare({ ...seat, price }, feePerPassenger, defaultSeatPrice);
  };

  const seatsTotal = regularSeats.reduce((sum, seat) => sum + seatPrice(seat), 0);
  const adjacentTotal = adjacentSeats.reduce((sum, seat) => sum + seatPrice(seat), 0);
  const total = seatsTotal + adjacentTotal;

  return {
    amount: total,
    total,
    totalPercentDiscount: '0%',
    totalBeforeDiscount: total,
    discount: 0,
    discountType: pricing.discountType || null,
    passengersCount: regularSeats.length,
    luggage: pricing.luggage || 0,
    taxes: pricing.taxes || 0,
    networks: pricing.networks || 0,
    insurance: pricing.insurance || 0,
    passengerBreakdown: {
      general: {
        count: regularSeats.length,
        total: seatsTotal,
        totalSpecialDiscount: seatsTotal,
        countWithSpecialDiscount: regularSeats.length,
        countWithoutSpecialDiscount: 0,
        normalTotal: 0,
        discount: 0,
        discountPercent: null,
        normalDiscountPercent: null,
      },
    },
    hasSingleTripDiscount: false,
    singleTripDiscount: null,
    // Seat-based pricing totals come from each `seat.price`, and those prices carry
    // no platform-usage fee (MID-747) breakdown, so there is nothing to disclose
    // here. The backend still charges the fee per ticket in this mode, so a tenant
    // combining the two would see a total below what is charged: the two features
    // are mutually exclusive until search emits the fee here (MID-804). No tenant
    // has both today — the fee needs `USE_SEAT_BASED_PRICING`, which fospina lacks.
    fixedFee: null,
  };
};

/**
 * Builds pricing for PurchasePricingTrip when selected-seat totals are authoritative.
 * Seat prices are all-in quotes, so additive breakdown rows must stay hidden unless
 * the backend provides a reliable per-seat allocation for them.
 * @param {object} options - Options accepted by getTripPricingBySeatPrice.
 * @returns {object} Renderer-compatible pricing with the all-in seat total.
 */
const getPreCheckoutSeatTripPricing = (options) => ({
  ...getTripPricingBySeatPrice(options),
  luggage: 0,
  taxes: 0,
  networks: 0,
  insurance: 0,
  serviceFees: 0,
  toll: 0,
  boardingTax: 0,
  managementCosts: 0,
});

/**
 * Gets the passenger total for a selected-seat position.
 * @param {object} options - Helper options.
 * @param {number} options.index - Selected-seat position.
 * @param {number} options.selectedCount - Total selected seats for the trip.
 * @param {object} options.defaultPricing - Trip pricing used as fallback.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @param {boolean} [options.showFinalPrice] - Whether this purchase is in the final arm (BOOK-168).
 * @returns {number} Passenger total for the selected-seat position.
 */
export const getPassengerTotalBySelectionIndex = ({
  index,
  selectedCount,
  defaultPricing: rawDefaultPricing = {},
  mixDiscountFaresByAvailability = false,
  showFinalPrice = false,
}) => {
  // BOOK-168: every branch below picks a different fare for this seat -- the discount, a fallback
  // discount, the pre-discount one -- so the view hands each of them its own companion. This is
  // the one per-seat derivation the sidebar rows, the leg header and the grand total share.
  const defaultPricing = finalPricingView(rawDefaultPricing, showFinalPrice);
  const defaultTotal = Number(defaultPricing?.total || 0);
  const normalTotal = Number(defaultPricing?.totalBeforeDiscount ?? defaultPricing?.total ?? 0);
  const fareAllocation = resolveFareAllocation({
    pricing: defaultPricing,
    selectedCount,
    mixDiscountFaresByAvailability,
  });
  const {
    discountAvailability,
    discountAvailabilityExceeded,
    shouldMixDiscountFares,
    perSeatTotals,
    fallbackDiscount,
  } = fareAllocation;
  const hasValidDiscount = Boolean(
    defaultPricing.providerDiscount && discountAvailability !== null,
  );
  const normalizedSelectedCount = Number(selectedCount);

  if (
    discountAvailabilityExceeded &&
    Number.isFinite(normalizedSelectedCount) &&
    normalizedSelectedCount > discountAvailability
  ) {
    if (shouldMixDiscountFares) return Number(perSeatTotals[index] ?? normalTotal);

    return Number(fallbackDiscount?.total ?? perSeatTotals[index] ?? normalTotal);
  }

  if (!hasValidDiscount || index < discountAvailability) return defaultTotal;

  return normalTotal;
};

/**
 * Calculate selected seats total for pre-checkout pricing.
 * Seat layout prices are authoritative when seat-based pricing is enabled. Canonical
 * BFO is the narrow exception: its selected-seat quote is authoritative whenever the
 * caller has already confirmed a supported journey, without another Funnel flag.
 * @param {object} options - The options for calculating selected seats total.
 * @param {Array} options.seats - Selected seats.
 * @param {object} options.defaultPricing - Trip pricing used as the per-passenger fallback.
 * @param {number} [options.feePerPassenger] - INS-1288 delta to take out of each seat fare.
 * @param {boolean} options.useSeatBasedPricing - Whether seat prices should drive totals.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @param {boolean} [options.showFinalPrice] - Whether this purchase is in the final arm (BOOK-168).
 * @param {boolean} options.useBaseFareOverride - Whether supported canonical BFO may apply.
 * @returns {number} Selected seats total.
 */
export const getSelectedSeatsTotal = ({
  seats = [],
  defaultPricing = {},
  useSeatBasedPricing = false,
  mixDiscountFaresByAvailability = false,
  showFinalPrice = false,
  // INS-1288: the shared per-passenger delta, for the seat-based branch below. `defaultPricing`
  // arrives already netted, so the delta cannot be recovered from it -- `netPricingView` keeps
  // `displayTotal`, which makes a second pass derive 0. It has to come from the caller, which still
  // holds the raw pricing. Defaults to 0, so every caller that does not pass it is unchanged.
  feePerPassenger = 0,
  useBaseFareOverride = false,
}) => {
  const defaultTotal = Number(defaultPricing?.total || 0);
  const useEffectiveBfoSeatPrice = useBaseFareOverride && isBfoPricing(defaultPricing);

  if (!seats.length) return 0;
  if (!useSeatBasedPricing && !useEffectiveBfoSeatPrice) {
    return seats.reduce(
      (sum, _seat, index) =>
        sum +
        getPassengerTotalBySelectionIndex({
          index,
          selectedCount: seats.length,
          defaultPricing,
          mixDiscountFaresByAvailability,
          showFinalPrice,
        }),
      0,
    );
  }

  // Seat-map prices and BFO seat quotes carry no companion. The final arm never reaches this
  // branch on a seat-based tenant: the rule keeps it in control, since hiding its per-seat prices
  // would break its totals (D10).
  return seats.reduce((sum, seat) => {
    const price = useEffectiveBfoSeatPrice
      ? resolveBfoEffectiveSeatPrice({ pricing: defaultPricing, seat })
      : seat.price;
    return sum + netSeatFare({ ...seat, price }, feePerPassenger, defaultTotal);
  }, 0);
};

/**
 * Sums the fixed platform-usage fee (MID-747) across every selected seat.
 * The API sends per-seat unit amounts, so each trip contributes its unit fee
 * once per selected seat. The unit of the fee is the *ticket*, not the passenger:
 * `Tentative::Purchase#fixed_fee_amount_total` multiplies by `tickets.count`, and
 * an adjacent seat is a ticket of its own (`Ticket#adjacent_seat?`), so it is
 * counted here too. Leaving it out would disclose one fee where two get charged.
 * @param {Array<{fixedFee: object, seats: Array}>} legs - Per-trip unit fee and selected seats.
 * @returns {{amount: number, tax: number}|null} Fee totals, or null when there is no fee.
 */
export const getFixedFeeTotals = (legs = []) => {
  const { amount, tax } = legs.reduce(
    (acc, leg) => {
      const seatCount = (leg?.seats || []).length;

      return {
        amount: acc.amount + (leg?.fixedFee?.amount || 0) * seatCount,
        tax: acc.tax + (leg?.fixedFee?.tax || 0) * seatCount,
      };
    },
    { amount: 0, tax: 0 },
  );

  return amount > 0 ? { amount, tax } : null;
};

/**
 * Takes the platform-usage fee (MID-747) out of a trip subtotal.
 * The API bakes the per-seat fee into `pricing.total`, but the payment breakdown
 * lists the fee and its tax in their own rows *below* the trip subtotal, so leaving
 * it inside the subtotal shows it twice. `amount` is left alone on purpose: the API
 * sends category amounts without the fee, so the passenger rows above the subtotal
 * already exclude it. The fee is netted with the same totals the rows display, which
 * keeps the block adding up.
 *
 * This subtraction is unconditional, and that is only sound because it is disjoint from the
 * INS-1288 netting (`utils/trips/displayedPrice#netPricingView`). That one takes out
 * `total - displayTotal` — the convenience fee the provider folds into the price (taxa de
 * conveniência) — while the platform-usage fee is added by us on top and stays inside both
 * `total` and `displayTotal`, which is why it still has to come out here after the netting.
 * The payload carries no signal saying whether a fee is already out of `displayTotal`, so there is
 * nothing to condition on; the contract is the guard. The two only ever meet on a tenant that
 * has both the backend `SHOW_TICKET_PRICE_WITHOUT_FEES` flag and a per-seat `fixed_fee` in its
 * search payload — no tenant has both today. The composition is pinned by
 * `usePricingBeforeCheckout.test.js`, "a display total combined with the platform-usage fee";
 * if the backend ever nets the fee into `displayTotal` too, that suite is what has to change.
 * @param {object} tripPricing - Trip pricing as returned by `getTripPricing`.
 * @param {{amount: number, tax: number}|null} feeTotals - Fee totals for that same trip.
 * @returns {object} The trip pricing with a fee-free `total`.
 */
export const withoutFixedFeeTotal = (tripPricing, feeTotals) => {
  if (!feeTotals) return tripPricing;

  return {
    ...tripPricing,
    total: Math.max((tripPricing.total || 0) - feeTotals.amount - feeTotals.tax, 0),
  };
};

/**
 * Custom hook for calculating pricing before checkout.
 * @param {Object} options - The options for the hook.
 * @param {boolean} options.isRoundTrip - The flag to indicate if the trip is a round trip.
 * @returns {Object} Pricing and selection state for pre-checkout.
 * @returns {Object} return.selectedSeats - Selected seats per direction (departs, returns).
 * @returns {number} return.passengersCount - Number of passengers (from depart selected seats).
 * @returns {boolean} return.displayPricingBeforeCheckout - Whether to show pricing before checkout step.
 * @returns {Object} return.departTripPricing - Calculated pricing for departure trip, with a `total` that excludes the platform-usage fee.
 * @returns {Object} return.returnTripPricing - Calculated pricing for return trip (or zeroed if one-way), with a `total` that excludes the platform-usage fee.
 * @returns {number} return.purchaseTotal - Total price for selected seats when displaying before checkout, fee included.
 * @returns {Object} return.fixedFeeTotals - Platform-usage fee totals ({ amount, tax }) for the whole selection, or null.
 * @returns {boolean} return.showFinalPrice - Whether this purchase paints the markup-inclusive price (BOOK-168), for the sidebar rows to share.
 */
const usePricingBeforeCheckout = ({ isRoundTrip }) => {
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const pricingBeforeCheckoutIsOn = features.PRICING_BEFORE_CHECKOUT;
  // Selecting only the journey fields keeps Redux references stable while still giving the BFO
  // route gate every exclusion it needs. Conversions are memoized against Immutable branches.
  const departsState = useSelector((state) => state.purchase.get('departs'));
  const returnsState = useSelector((state) => state.purchase.get('returns'));
  const isExchange = useSelector((state) => Boolean(state.purchase.get('isExchange')));
  const openTicket = useSelector((state) => Boolean(state.purchase.get('openTicket')));
  const quickPurchase = useSelector((state) => Boolean(state.purchase.get('quickPurchase')));
  const bulkTicket = useSelector((state) => Boolean(state.purchase.get('bulkTicket')));
  const departs = useMemo(() => departsState?.toJS(), [departsState]);
  const returns = useMemo(() => returnsState?.toJS(), [returnsState]);
  const purchase = useMemo(
    () => ({ departs, returns, isExchange, openTicket, quickPurchase, bulkTicket }),
    [departs, returns, isExchange, openTicket, quickPurchase, bulkTicket],
  );
  const location = useLocation();
  const purchaseStep = getPurchaseStepName(location.pathname);
  const isBeforeCheckout = PRE_CHECKOUT_STEPS.includes(purchaseStep);
  const passengersCount = departs?.selectedSeats?.length || 0;
  const displayPricingBeforeCheckout = isBeforeCheckout && pricingBeforeCheckoutIsOn;
  const useSeatPriceTotals = Boolean(features.USE_SEAT_BASED_PRICING);
  const mixDiscountFaresByAvailability = useMixDiscountFaresByAvailability();
  const departPricingKey = purchaseLegPricingKey({ way: 'departs', isRoundTrip });
  // INS-1288 A/B: the seat panel renders inside the results view for seats-on-results tenants,
  // so it must land in the same arm as the card behind it. Resolved from the depart leg -- the
  // one the card shows -- and applied to both legs so a round trip cannot straddle arms.
  const netFeesForDisplay = usePriceWithoutFeesVariant(
    hasDisplayTotal(departs?.fragments?.[0]?.[departPricingKey]),
  );
  const useBaseFareOverride = isBfoSupportedJourney({
    purchase,
    features,
    brand: env?.brand,
  });
  // BOOK-168: the purchase's final-price arm, gated on either leg's chosen pricing and only on the
  // steps that show this estimate, so checkout and the complete page (which mount this hook
  // through `PurchasePricing`) never evaluate it. The rule refuses under INS-1288, so the two never
  // meet.
  const showFinalPrice = usePreCheckoutFinalPrice();

  // Memoize all pricing calculations to prevent recalculation on every render
  const result = useMemo(() => {
    const departSelectedSeats = departs?.selectedSeats || [];
    const returnSelectedSeats = returns?.selectedSeats || [];

    // Resolve the fare first, then apply display-only netting. Seat-authoritative paths use the
    // same raw pricing so BFO seat quotes and the provider fee delta compose in one calculation.
    const rawDepartPricing = departs?.fragments?.[0]?.[departPricingKey];
    const departPricingPerPassenger = netPricingView(rawDepartPricing, netFeesForDisplay);
    const useDepartSeatTripPricing =
      useSeatPriceTotals || (useBaseFareOverride && isBfoPricing(departPricingPerPassenger));
    const departTripPricing = useDepartSeatTripPricing
      ? getPreCheckoutSeatTripPricing({
          selectedSeats: departSelectedSeats,
          pricing: rawDepartPricing,
          netFeesForDisplay,
          useBaseFareOverride,
        })
      : getTripPricing({
          pricing: departPricingPerPassenger,
          passengersCount,
          mixDiscountFaresByAvailability,
          netFeesForDisplay,
          showFinalPrice,
        });

    // The return leg only earns the round-trip fare when the provider sells both legs.
    const returnsFragment = (returns?.fragments || [])[0];
    const returnPricingKey = purchaseLegPricingKey({
      way: 'returns',
      earnsRoundTripFare: returnLegEarnsRoundTripFare({
        validateRoundTripDiscount: features.VALIDATE_ROUND_TRIP_DISCOUNT,
        departFragments: departs?.fragments,
        returnFragments: returns?.fragments,
      }),
    });
    const rawReturnPricing = returnsFragment?.[returnPricingKey];
    const returnPricingPerPassenger = netPricingView(rawReturnPricing, netFeesForDisplay);
    let returnTripPricing = {
      amount: 0,
      discount: 0,
      total: 0,
      luggage: 0,
      taxes: 0,
      networks: 0,
      insurance: 0,
      // One way: there is no return trip to charge a platform-usage fee for.
      fixedFee: null,
    };
    if (returnPricingPerPassenger) {
      const useReturnSeatTripPricing =
        useSeatPriceTotals || (useBaseFareOverride && isBfoPricing(returnPricingPerPassenger));
      returnTripPricing = useReturnSeatTripPricing
        ? getPreCheckoutSeatTripPricing({
            selectedSeats: returnSelectedSeats,
            pricing: rawReturnPricing,
            netFeesForDisplay,
            useBaseFareOverride,
          })
        : getTripPricing({
            pricing: returnPricingPerPassenger,
            passengersCount,
            mixDiscountFaresByAvailability,
            netFeesForDisplay,
            showFinalPrice,
          });
    }

    let precheckoutSeatsTotal = 0;
    const hasSelectedSeats = departSelectedSeats.length > 0 || returnSelectedSeats.length > 0;

    if (displayPricingBeforeCheckout && hasSelectedSeats) {
      const departTotalPrice = getSelectedSeatsTotal({
        seats: departSelectedSeats,
        defaultPricing: departPricingPerPassenger,
        useSeatBasedPricing: useSeatPriceTotals,
        mixDiscountFaresByAvailability,
        feePerPassenger: netFeePerPassenger(rawDepartPricing, netFeesForDisplay),
        useBaseFareOverride,
        showFinalPrice,
      });
      const returnTotalPrice = getSelectedSeatsTotal({
        seats: returnSelectedSeats,
        defaultPricing: returnPricingPerPassenger,
        useSeatBasedPricing: useSeatPriceTotals,
        mixDiscountFaresByAvailability,
        feePerPassenger: netFeePerPassenger(rawReturnPricing, netFeesForDisplay),
        useBaseFareOverride,
        showFinalPrice,
      });

      precheckoutSeatsTotal = departTotalPrice + returnTotalPrice;
    }

    // Platform-usage fee (MID-747) per trip. Before checkout the purchase has no
    // ticket per selected seat yet, so its purchase-level fee totals lag behind;
    // deriving them here keeps the payment breakdown in sync with the total this
    // same hook computes and with the seats tooltip.
    //
    // Exchanges charge no fee (`Tentative::Purchase#fixed_fee_unit` returns nil for
    // them), but search pricing has no idea it is being read in an exchange flow and
    // sends the fee anyway, so the guard lives here. It only suppresses the
    // *disclosure*: the trip total still comes from search and is therefore still fee
    // inclusive, which MID-806 tracks. The payment step needs no guard — it reads the
    // purchase's own `fixed_fee`, which the backend already omits on an exchange.
    const feeOf = (tripPricing) => (isExchange ? null : tripPricing.fixedFee);
    const departFeeLeg = { fixedFee: feeOf(departTripPricing), seats: departSelectedSeats };
    const returnFeeLeg = { fixedFee: feeOf(returnTripPricing), seats: returnSelectedSeats };

    return {
      selectedSeats: {
        departs: departSelectedSeats,
        returns: returnSelectedSeats,
      },
      passengersCount,
      displayPricingBeforeCheckout,
      departTripPricing: withoutFixedFeeTotal(departTripPricing, getFixedFeeTotals([departFeeLeg])),
      returnTripPricing: withoutFixedFeeTotal(returnTripPricing, getFixedFeeTotals([returnFeeLeg])),
      purchaseTotal: precheckoutSeatsTotal,
      fixedFeeTotals: getFixedFeeTotals([departFeeLeg, returnFeeLeg]),
      showFinalPrice,
    };
  }, [
    departPricingKey,
    departs,
    returns,
    isExchange,
    passengersCount,
    displayPricingBeforeCheckout,
    useSeatPriceTotals,
    mixDiscountFaresByAvailability,
    netFeesForDisplay,
    features.VALIDATE_ROUND_TRIP_DISCOUNT,
    useBaseFareOverride,
    showFinalPrice,
  ]);

  return result;
};

export default usePricingBeforeCheckout;
